import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

/**
 * Returns a derived loading flag that stays `true` for at least `minDurationMs`
 * milliseconds after the real loading state transitions from `true` to `false`.
 *
 * ## Why this exists
 *
 * Fast network responses can cause `isLoading` to flip `true → false` within a
 * single React render batch, so the loading indicator never paints at all.  This
 * hook guarantees the indicator is visible for at least one full animation cycle,
 * giving users clear visual feedback regardless of response time.
 *
 * ## Behaviour
 *
 * - When `isLoading` becomes `true`: the returned flag immediately becomes `true`.
 * - When `isLoading` becomes `false`: the returned flag stays `true` for the
 *   remaining duration of `minDurationMs`, then flips to `false`.
 * - If loading restarts before the timer fires, the timer is cancelled and the
 *   flag remains `true` for the new loading cycle.
 *
 * @param isLoading   The raw loading boolean from the data store.
 * @param minDurationMs  Minimum visible duration in milliseconds (default 1500 —
 *   one full cycle of the `eag-grid-loading-slide` keyframe animation).
 */
export function useMinimumLoadingDuration(isLoading, t0) {
  const $ = _c(4);
  const minDurationMs = t0 === undefined ? 1500 : t0;
  const [showLoading, setShowLoading] = useState(isLoading);
  const timerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== isLoading || $[1] !== minDurationMs) {
    t1 = () => {
      if (isLoading) {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setShowLoading(true);
      } else {
        timerRef.current = setTimeout(() => {
          setShowLoading(false);
          timerRef.current = null;
        }, minDurationMs);
      }
      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    };
    t2 = [isLoading, minDurationMs];
    $[0] = isLoading;
    $[1] = minDurationMs;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return showLoading;
}